/**
 * Service Detail View
 *
 * Read-only view of a service within the Account page. Uses a uniform template
 * so all service types (NBN, Sonweb, mobile, etc.) can have consistent layouts.
 * NBN has the most fields and is grouped into logical sections.
 * Custom fields from Settings → Service Types are shown from type_specific_data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Grid,
  Chip,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  DriveFileMove as MoveIcon,
  ArrowForward as ArrowForwardIcon,
  StickyNote2 as NoteIcon,
  Assignment as CaseIcon,
  ConfirmationNumber as TicketIcon,
  EventNote as ReservationIcon,
  Visibility as RevealIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { AccountService, HARDCODED_ACCOUNT_SERVICE_FIELD_KEYS } from '../types/account';
import apiService from '../services/api';
import { OrderProduct } from '../types/order';
import {
  VM_ORDER_CATEGORY_SLUG,
  VM_ORDER_SUBCATEGORY_SLUG,
  isVmPlanServiceField,
  resolveVmPlanDisplay,
} from '../utils/virtualMachinePlan';
import { isMspAgreementServiceType } from '../utils/mspAgreement';
import MspServiceDocumentsTab from './MspServiceDocumentsTab';
import { isDomainNameServiceType } from '../utils/domainName';
import DomainRegistryPanel from './DomainRegistryPanel';
import { useAuth } from '../contexts/AuthContext';
import OpenCpanelButton, { canOpenCpanel } from './OpenCpanelButton';
import SupaConnectionStatus from './SupaConnectionStatus';
import SupaMonthlyUsage from './SupaMonthlyUsage';
import SupaIPoEStatus from './SupaIPoEStatus';
import SupaBuildingAddress from './SupaBuildingAddress';

const SERVICE_TYPE_LABELS: Record<string, string> = {
  nbn: 'NBN',
  mobile: 'Mobile',
  voice: 'Voice',
  sonweb: 'Sonweb',
  data: 'Data',
  supa_networks: 'Supa Networks',
};

const formatValue = (v: string | undefined | null): string => (v != null ? String(v).trim() : '') || '—';

function humanizeFieldKey(fieldKey: string): string {
  if (!fieldKey) return fieldKey;
  return fieldKey
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function formatSelectStoredValue(
  raw: unknown,
  options: { value: string; label?: string | null; sort_order?: number }[]
): string {
  if (raw === null || raw === undefined) return '';
  const s = typeof raw === 'object' ? JSON.stringify(raw) : String(raw).trim();
  if (!s) return '';
  const sorted = [...options].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const hit = sorted.find((o) => String(o.value) === s);
  if (hit) {
    const ol = hit.label != null ? String(hit.label).trim() : '';
    return ol || String(hit.value);
  }
  return s;
}

function formatDateStored(raw: string): string {
  const s = raw.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return raw;
    try {
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return s;
    }
  }
  return raw;
}

const MSP_SOURCE_CASE = 'case';
const MSP_SOURCE_TICKET = 'support_desk_ticket';

function mspActivityEventLabel(eventType: string): string {
  const m: Record<string, string> = {
    reservation_set: 'Hours reserved',
    reservation_updated: 'Reservation updated',
    reservation_cleared: 'Reservation cleared',
    note_added: 'Staff note',
  };
  return m[eventType] || eventType.replace(/_/g, ' ');
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: '#1e3a5c',
        px: 2,
        py: 0.75,
        mx: -3,
        mt: -3,
        mb: 2.5,
        borderRadius: '4px 4px 0 0',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}
      >
        {children}
      </Typography>
    </Box>
  );
}

interface DetailFieldProps {
  label: string;
  value: string | undefined | null;
}

function DetailField({ label, value }: DetailFieldProps) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
        {formatValue(value)}
      </Typography>
    </Grid>
  );
}

interface PasswordDetailFieldProps {
  label: string;
  hasValue: boolean;
  revealedValue: string | undefined;
  revealing: boolean;
  onToggle: () => void;
}

/** Stored encrypted ("enc:...") — never render the raw value. Masked by default with a
 * reveal toggle that decrypts on demand via a dedicated endpoint, since staff genuinely
 * need the plaintext to hand to a customer or key into a modem. */
function PasswordDetailField({ label, hasValue, revealedValue, revealing, onToggle }: PasswordDetailFieldProps) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontFamily: revealedValue ? 'monospace' : undefined }}>
          {!hasValue ? '—' : revealedValue !== undefined ? revealedValue : '••••••••'}
        </Typography>
        {hasValue && (
          <IconButton size="small" onClick={onToggle} disabled={revealing}>
            {revealing ? <CircularProgress size={14} /> : revealedValue !== undefined ? <HideIcon fontSize="inherit" /> : <RevealIcon fontSize="inherit" />}
          </IconButton>
        )}
      </Box>
    </Grid>
  );
}

interface DetailSectionProps {
  title: string;
  children: React.ReactNode;
}

function DetailSection({ title, children }: DetailSectionProps) {
  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <SectionLabel>{title}</SectionLabel>
      <Grid container spacing={2.5}>
        {children}
      </Grid>
    </Paper>
  );
}

interface ServiceDetailViewProps {
  service: AccountService;
  onBack: () => void;
  onEdit?: () => void;
  onMove?: () => void;
}

interface DynamicFieldDef {
  field_key: string;
  label: string;
  field_type: string;
  options: { value: string; label?: string | null; sort_order?: number }[];
}

const ServiceDetailView: React.FC<ServiceDetailViewProps> = ({ service, onBack, onEdit, onMove }) => {
  const [dynamicFieldDefs, setDynamicFieldDefs] = useState<DynamicFieldDef[]>([]);
  const [revealedFields, setRevealedFields] = useState<Record<string, string>>({});
  const [revealingField, setRevealingField] = useState<string | null>(null);
  const [mobilePlanName, setMobilePlanName] = useState<string | null>(null);
  const [linkLabels, setLinkLabels] = useState<Record<string, string>>({});
  const [vmPlanProducts, setVmPlanProducts] = useState<OrderProduct[]>([]);
  const [mspTab, setMspTab] = useState(0);
  const [mspBalance, setMspBalance] = useState<{
    total_grants: string;
    reserved_total: string;
    available_total: string;
    as_of: string;
    timezone: string;
  } | null>(null);
  const [mspActivity, setMspActivity] = useState<any[]>([]);
  const [mspActivityLoading, setMspActivityLoading] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const isMsp = isMspAgreementServiceType(service.service_type, service.service_type_name);
  const isDomain = isDomainNameServiceType(service.service_type, service.service_type_name);
  const { user } = useAuth();
  const canViewAuthInfo = !!user?.is_admin;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const types = await apiService.getServiceTypes(false);
        const id = (Array.isArray(types) ? types : []).find(
          (t: { code?: string }) => (t.code || '').trim().toLowerCase() === (service.service_type || '').trim().toLowerCase()
        )?.id;
        if (!id || cancelled) {
          if (!cancelled) setDynamicFieldDefs([]);
          return;
        }
        const t = await apiService.getServiceType(id);
        if (cancelled) return;
        // Fields that collide with a hardcoded AccountService column are shown
        // in the dedicated section above instead — don't display them twice.
        const fields = [...(t.fields || [])]
          .filter((f: { field_key: string }) => !HARDCODED_ACCOUNT_SERVICE_FIELD_KEYS.has(f.field_key))
          .sort(
            (a: { sort_order?: number }, b: { sort_order?: number }) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
          );
        setDynamicFieldDefs(
          fields.map((f: { field_key: string; label?: string; field_type?: string; options?: DynamicFieldDef['options'] }) => ({
            field_key: f.field_key,
            label: f.label != null ? String(f.label) : '',
            field_type: f.field_type || 'text',
            options: [...(f.options || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
          }))
        );
      } catch {
        if (!cancelled) setDynamicFieldDefs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [service.service_type, service.id]);

  const hasServiceLinkField = dynamicFieldDefs.some((d) => (d.field_type || '').toLowerCase() === 'account_service');
  useEffect(() => {
    if (!hasServiceLinkField || !service.account_id) {
      setLinkLabels({});
      return;
    }
    let cancelled = false;
    apiService
      .getAccountServices(service.account_id, { per_page: 500 })
      .then((res: { services?: { id: string; service_id: string; service_type: string; service_type_name?: string | null }[] }) => {
        if (cancelled) return;
        const m: Record<string, string> = {};
        (res.services || []).forEach((x) => {
          m[x.id] = `${x.service_type_name || x.service_type} · ${x.service_id}`;
        });
        setLinkLabels(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [hasServiceLinkField, service.account_id]);

  useEffect(() => {
    if ((service.service_type || '').toLowerCase() !== 'virtual_machine') {
      setVmPlanProducts([]);
      return;
    }
    let cancelled = false;
    apiService
      .getOrderProducts({
        category: VM_ORDER_CATEGORY_SLUG,
        subcategory: VM_ORDER_SUBCATEGORY_SLUG,
      })
      .then((rows) => {
        if (!cancelled) setVmPlanProducts(rows);
      })
      .catch(() => {
        if (!cancelled) setVmPlanProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [service.service_type]);

  const loadMspBalance = useCallback(() => {
    if (!isMsp) return;
    apiService.getMspBalance(service.id).then(setMspBalance).catch(() => setMspBalance(null));
  }, [isMsp, service.id]);

  const loadMspActivity = useCallback(() => {
    if (!isMsp) return;
    setMspActivityLoading(true);
    apiService
      .getMspActivity(service.id, { page: 1, per_page: 100 })
      .then((r: any) => {
        setMspActivity(r.items || []);
      })
      .catch(() => setMspActivity([]))
      .finally(() => setMspActivityLoading(false));
  }, [isMsp, service.id]);

  const handleRevealField = async (fieldKey: string) => {
    if (fieldKey in revealedFields) {
      setRevealedFields((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      return;
    }
    setRevealingField(fieldKey);
    try {
      const { value } = await apiService.revealServicePasswordField(service.id, fieldKey);
      setRevealedFields((prev) => ({ ...prev, [fieldKey]: value }));
    } catch {
      setRevealedFields((prev) => ({ ...prev, [fieldKey]: '(could not decrypt)' }));
    } finally {
      setRevealingField(null);
    }
  };

  useEffect(() => {
    const planId = (service.type_specific_data as Record<string, unknown> | null | undefined)?.mobile_plan_id;
    if (service.service_type !== 'mobile' || typeof planId !== 'string' || !planId) {
      setMobilePlanName(null);
      return;
    }
    let cancelled = false;
    apiService
      .getOrderProducts({ category: 'mobile' })
      .then((products: { id: string; name: string }[]) => {
        if (!cancelled) setMobilePlanName(products.find((p) => p.id === planId)?.name ?? null);
      })
      .catch(() => {
        if (!cancelled) setMobilePlanName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [service.service_type, service.type_specific_data]);

  useEffect(() => {
    loadMspBalance();
  }, [loadMspBalance]);

  useEffect(() => {
    if (isMsp && mspTab === 1) loadMspActivity();
  }, [isMsp, mspTab, loadMspActivity]);

  const typeLabel =
    service.service_type_name?.trim() || SERVICE_TYPE_LABELS[service.service_type] || service.service_type;
  const isNbn = service.service_type === 'nbn';
  const isSonweb = service.service_type === 'sonweb';
  const isSupa = service.service_type === 'supa_networks';
  const nbnPlanDisplay = isNbn && service.nbn_plan_id ? (service.nbn_plan_name ?? service.nbn_plan_id) : null;

  const tsd = service.type_specific_data;
  const hasTypeSpecificValues =
    tsd && typeof tsd === 'object' && !Array.isArray(tsd) && Object.keys(tsd).length > 0;
  const showDynamicSection = dynamicFieldDefs.length > 0;

  const domainAccountRecord =
    isDomain &&
    (Boolean(service.upstream_carrier?.trim()) || Boolean(service.notes?.trim()));

  const supaConnectionUsername: string | null = (() => {
    if (service.service_type !== 'supa_networks') return null;
    if (!tsd || typeof tsd !== 'object') return null;
    const data = tsd as Record<string, unknown>;
    if (data.auth_type !== 'PPPoE') return null;
    return typeof data.pppoe_username === 'string' && data.pppoe_username ? data.pppoe_username : null;
  })();

  const supaIPoEMac: string | null = (() => {
    if (service.service_type !== 'supa_networks') return null;
    if (!tsd || typeof tsd !== 'object') return null;
    const data = tsd as Record<string, unknown>;
    if (data.auth_type !== 'IPoE') return null;
    return typeof data.cpe_mac_address === 'string' && data.cpe_mac_address ? data.cpe_mac_address : null;
  })();

  const supaIPoEAssignedIp: string | null = (() => {
    if (service.service_type !== 'supa_networks') return null;
    if (!tsd || typeof tsd !== 'object') return null;
    const data = tsd as Record<string, unknown>;
    return typeof data.assigned_ip === 'string' && data.assigned_ip ? data.assigned_ip : null;
  })();

  const overviewSection = (
    <>
      {!isDomain && (
        <DetailSection title="Overview">
          <DetailField label="Service ID" value={service.service_id} />
          <DetailField label="Status" value={service.status} />
          {isMsp && mspBalance && (
            <>
              <DetailField label="MSP hours available" value={mspBalance.available_total} />
              <DetailField label="MSP hours reserved" value={mspBalance.reserved_total} />
              <DetailField label="MSP grant pool (rolling)" value={mspBalance.total_grants} />
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Balance as of {mspBalance.as_of} ({mspBalance.timezone})
                </Typography>
              </Grid>
            </>
          )}
          {!isMsp && <DetailField label="Upstream Carrier" value={service.upstream_carrier} />}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Notes
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {formatValue(service.notes)}
            </Typography>
          </Grid>
        </DetailSection>
      )}
    </>
  );

  const dynamicFieldsSection = (
    <>
      {showDynamicSection && !isDomain && (
        <DetailSection title={service.service_type_name?.trim() || 'Service type details'}>
          {dynamicFieldDefs.map((def) => {
            const raw =
              tsd && typeof tsd === 'object' && !Array.isArray(tsd)
                ? (tsd as Record<string, unknown>)[def.field_key]
                : undefined;
            const displayLabel = def.label.trim() ? def.label.trim() : humanizeFieldKey(def.field_key);
            if ((def.field_type || 'text').toLowerCase() === 'password') {
              return (
                <PasswordDetailField
                  key={def.field_key}
                  label={displayLabel}
                  hasValue={typeof raw === 'string' && raw.length > 0}
                  revealedValue={revealedFields[def.field_key]}
                  revealing={revealingField === def.field_key}
                  onToggle={() => handleRevealField(def.field_key)}
                />
              );
            }
            let display =
              raw === null || raw === undefined ? '' : typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
            if (
              (service.service_type || '').toLowerCase() === 'virtual_machine' &&
              isVmPlanServiceField(def.field_key, def.label)
            ) {
              display = resolveVmPlanDisplay(display, vmPlanProducts);
            } else if ((def.field_type || 'text').toLowerCase() === 'account_service' && display) {
              display = linkLabels[display] || 'Linked service';
            } else if ((def.field_type || 'text').toLowerCase() === 'select' && def.options.length > 0) {
              display = formatSelectStoredValue(raw, def.options);
            } else if ((def.field_type || 'text').toLowerCase() === 'date' && display) {
              display = formatDateStored(display);
            }
            return <DetailField key={def.field_key} label={displayLabel} value={display} />;
          })}
        </DetailSection>
      )}
    </>
  );

  const overviewBody = (
    <>
      {overviewSection}

      {domainAccountRecord && (
        <DetailSection title="Account record">
          {Boolean(service.upstream_carrier?.trim()) && (
            <DetailField label="Upstream carrier" value={service.upstream_carrier} />
          )}
          {Boolean(service.notes?.trim()) && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Notes
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                {formatValue(service.notes)}
              </Typography>
            </Grid>
          )}
        </DetailSection>
      )}

      {isDomain && <DomainRegistryPanel service={service} canViewAuthInfo={canViewAuthInfo} />}

      {isNbn && (
        <>
          <DetailSection title="Plan & Technology">
            <DetailField label="Plan" value={nbnPlanDisplay} />
            <DetailField label="Technology Type" value={service.nbn_technology_type} />
            <DetailField label="Authentication Type" value={service.nbn_authentication_type} />
            <DetailField label="Service Class" value={service.nbn_service_class} />
            <DetailField label="NBN SLA" value={service.nbn_sla} />
          </DetailSection>
          <DetailSection title="IP Configuration">
            <DetailField label="IP Address Type" value={service.nbn_ip_address_type} />
            <DetailField label="IP Address" value={service.nbn_ip_address} />
          </DetailSection>
          <DetailSection title="Carrier Identifiers">
            <DetailField label="WSM Service ID" value={service.wsm_service_id} />
            <DetailField label="NBN Carrier ID" value={service.nbn_carrier_id} />
            <DetailField label="Vanity Identifier" value={service.nbn_vanity_identifier} />
            <DetailField label="NBN AVC" value={service.nbn_avc} />
            <DetailField label="NBN POI" value={service.nbn_poi} />
          </DetailSection>
          <DetailSection title="Port Configuration">
            <DetailField label="Mapping" value={service.nbn_mapping} />
            {(service.nbn_technology_type === 'FTTP' || service.nbn_technology_type === 'FW') && (
              <DetailField label="UNID Port" value={service.nbn_unid_port} />
            )}
          </DetailSection>
        </>
      )}

      {isSonweb && (
        <DetailSection title="Hosting Details">
          <DetailField label="Primary Domain" value={service.primary_domain} />
          <DetailField label="Web Server" value={service.web_server} />
          <DetailField label="Web Hosting Plan" value={service.web_hosting_plan} />
          <DetailField label="Panel Type" value={service.panel_type} />
          {user?.is_admin && canOpenCpanel(service) && (
            <Grid item xs={12}>
              <OpenCpanelButton serviceId={service.id} />
            </Grid>
          )}
        </DetailSection>
      )}

      {dynamicFieldsSection}

      {!isNbn && !isSonweb && !isDomain && !showDynamicSection && !hasTypeSpecificValues && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No additional details for this service type yet.
          </Typography>
        </Paper>
      )}

      {!isNbn && !isSonweb && !isDomain && !showDynamicSection && hasTypeSpecificValues && (
        <DetailSection title="Service type details">
          {Object.entries(tsd as Record<string, unknown>).map(([key, val]) => (
            <DetailField
              key={key}
              label={key === 'mobile_plan_id' ? 'Mobile plan' : humanizeFieldKey(key)}
              value={
                key === 'mobile_plan_id' && service.service_type === 'mobile'
                  ? mobilePlanName ?? String(val ?? '')
                  : val === null || val === undefined ? '' : typeof val === 'object' ? JSON.stringify(val) : String(val)
              }
            />
          ))}
        </DetailSection>
      )}
    </>
  );

  const activityBody = (
    <Paper sx={{ p: 3 }}>
      <SectionLabel>Agreement activity</SectionLabel>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button size="small" variant="contained" onClick={() => setNoteOpen(true)}>
          Add note
        </Button>
      </Box>
      {mspActivityLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : mspActivity.length === 0 ? (
        <Typography color="text.secondary">No activity yet.</Typography>
      ) : (
        <List dense>
          {mspActivity.map((row: any) => {
            const p = row.payload || {};
            const et = String(row.event_type || '');
            const refNum = (p.case_number || p.ticket_number || '') as string;
            const subject = String((p.source_subject || p.subject || '') as string).trim();
            const sourceId = p.source_id as string | undefined;
            const sourceType = p.source_type as string | undefined;

            let linkTo: string | null = null;
            if (sourceId && sourceType === MSP_SOURCE_CASE) linkTo = `/crm/cases/${sourceId}`;
            else if (sourceId && sourceType === MSP_SOURCE_TICKET) linkTo = `/crm/support-desk/${sourceId}`;

            let RowIcon = ReservationIcon;
            if (et === 'note_added') RowIcon = NoteIcon;
            else if (sourceType === MSP_SOURCE_CASE) RowIcon = CaseIcon;
            else if (sourceType === MSP_SOURCE_TICKET) RowIcon = TicketIcon;

            const primaryLine =
              et === 'note_added'
                ? String(p.message || 'Note')
                : `${mspActivityEventLabel(et)}${refNum ? ` — ${refNum}` : ''}`;

            const secondary = `${row.created_by_name || 'System'} — ${new Date(row.created_at).toLocaleString()}`;

            return (
              <ListItem
                key={row.id}
                alignItems="flex-start"
                secondaryAction={
                  linkTo ? (
                    <IconButton
                      component={RouterLink}
                      to={linkTo}
                      edge="end"
                      size="small"
                      aria-label="Open case or ticket"
                    >
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  ) : undefined
                }
                sx={{ pr: linkTo ? 7 : 1 }}
              >
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  <RowIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                        {primaryLine}
                      </Typography>
                      {et !== 'note_added' && subject ? (
                        <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {subject}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                  secondary={secondary}
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <Dialog open={noteOpen} onClose={() => !noteSaving && setNoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add staff note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            minRows={3}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteOpen(false)} disabled={noteSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={noteSaving || !noteText.trim()}
            onClick={async () => {
              setNoteSaving(true);
              try {
                await apiService.postMspActivityNote(service.id, noteText.trim());
                setNoteText('');
                setNoteOpen(false);
                loadMspActivity();
                loadMspBalance();
              } catch (e) {
                console.error(e);
              } finally {
                setNoteSaving(false);
              }
            }}
          >
            {noteSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton onClick={onBack} size="small" aria-label="Back to services" sx={{ bgcolor: 'action.hover' }}>
              <BackIcon fontSize="small" />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ lineHeight: 1.2 }}>
                {typeLabel} Service
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {service.service_id}
              </Typography>
            </Box>
            <Chip
              label={service.status}
              size="small"
              color={service.status === 'active' ? 'success' : service.status === 'suspended' ? 'warning' : 'default'}
              sx={{ fontWeight: 600, ml: 0.5 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {onMove && (
              <Button startIcon={<MoveIcon />} variant="outlined" size="small" onClick={onMove}>
                Move to account
              </Button>
            )}
            {onEdit && (
              <Button startIcon={<EditIcon />} variant="contained" size="small" onClick={onEdit}>
                Edit
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* MSP tabs */}
      {isMsp && (
        <Paper sx={{ px: 3, pt: 1, pb: 0, mb: 2 }}>
          <Tabs value={mspTab} onChange={(_, v) => setMspTab(v)}>
            <Tab label="Overview" />
            <Tab label="Activity" />
            <Tab label="Documents" />
          </Tabs>
        </Paper>
      )}

      {(!isMsp || mspTab === 0) && (
        isSupa ? (
          <>
            {/* Overview paired with Building & Service Address — overviewSection and
                dynamicFieldsSection are rendered directly below instead of via the
                full overviewBody, since the remainder of overviewBody (account record,
                nbn/sonweb blocks, fallback blocks) never applies to supa_networks. */}
            <Grid container spacing={2.5} sx={{ mb: 0 }}>
              <Grid item xs={12} md={6}>{overviewSection}</Grid>
              <Grid item xs={12} md={6}><SupaBuildingAddress service={service} /></Grid>
            </Grid>

            {/* Supa Networks fields paired with connection status — IPoE or PPPoE,
                whichever applies, occupies the same slot. */}
            <Grid container spacing={2.5} sx={{ mb: 0 }}>
              <Grid item xs={12} md={6}>{dynamicFieldsSection}</Grid>
              <Grid item xs={12} md={6}>
                {supaIPoEMac && <SupaIPoEStatus macAddress={supaIPoEMac} assignedIp={supaIPoEAssignedIp} />}
                {supaConnectionUsername && <SupaConnectionStatus pppoeUsername={supaConnectionUsername} />}
              </Grid>
            </Grid>

            {/* Monthly usage (PPPoE only) — unpaired, full width */}
            {supaConnectionUsername && <SupaMonthlyUsage pppoeUsername={supaConnectionUsername} />}
          </>
        ) : overviewBody
      )}

      {isMsp && mspTab === 1 && activityBody}
      {isMsp && mspTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <SectionLabel>Documents</SectionLabel>
          <MspServiceDocumentsTab serviceId={service.id} />
        </Paper>
      )}
    </Box>
  );
};

export default ServiceDetailView;
